import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, User, HelpCircle, LogOut, Home } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { cn } from '../../lib/utils';
import { productsApi } from '../../services/api';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const FreeProductAvatarMenu = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { getThemeColors } = useSafeMode();
  const [open, setOpen] = useState(false);
  const [productId, setProductId] = useState(null);
  const colors = getThemeColors();

  useEffect(() => {
    productsApi.list(false).then(res => {
      const products = res.data?.products || res.data || [];
      if (products.length > 0) setProductId(products[0].product_id);
    }).catch(() => {});
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleHome = () => {
    navigate(productId ? `/products/${productId}` : '/products/new');
  };

  return (
    <div data-testid="free-product-avatar-menu" className="shrink-0">
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <button className={cn(
            "flex items-center gap-2 px-2.5 py-1.5 rounded-full border transition-all",
            "hover:bg-white/10",
            colors.bgSecondary,
            colors.borderPrimary,
          )}>
            <div className={cn(
              "w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold",
              colors.avatarBg || "bg-gradient-to-br from-violet-500 to-purple-600",
              colors.textOnAccent || "text-white"
            )}>
              {user?.name?.charAt(0) || 'U'}
            </div>
            <span className={cn("text-sm font-medium max-w-[120px] truncate", colors.textPrimary)}>
              {user?.name}
            </span>
            <ChevronDown className={cn("w-3 h-3", colors.textMuted)} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="start" 
          side="bottom" 
          sideOffset={8}
          className={cn("w-56", colors.bgCard, colors.borderPrimary)}
        >
          <div className={cn("px-2 py-1.5", colors.textMuted)}>
            <p className={cn("text-sm font-medium", colors.textPrimary)}>{user?.name}</p>
            <p className="text-xs truncate">{user?.email}</p>
          </div>
          <DropdownMenuSeparator className={colors.borderPrimary} />
          <DropdownMenuItem 
            onClick={handleHome}
            data-testid="free-menu-home"
            className={cn("cursor-pointer", colors.bgHover)}
          >
            <Home className="w-4 h-4 mr-2" />
            My Product
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => navigate('/settings')}
            className={cn("cursor-pointer", colors.bgHover)}
          >
            <User className="w-4 h-4 mr-2" />
            Account Settings
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => window.open('/help', '_blank')}
            className={cn("cursor-pointer", colors.bgHover)}
          >
            <HelpCircle className="w-4 h-4 mr-2" />
            Help
          </DropdownMenuItem>
          <DropdownMenuSeparator className={colors.borderPrimary} />
          <DropdownMenuItem 
            onClick={handleLogout}
            className={cn("cursor-pointer text-red-500 focus:text-red-500 focus:bg-red-500/10")}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default FreeProductAvatarMenu;
